// src/pages/ErderanlageBlock.jsx
// Modulare Erderanlagen-Darstellung mit waagerechter Schiene + Anschluss-Pins.
// - Schienen + Anschluss-Bezeichnungen = Stammdaten der Station
// - Ohm-Werte = pro Wartung (Tabelle protokoll_erderanlage_messungen)
// - Bei Ohm-Wert ueber dem Grenzwert wird der Anschluss rot markiert (Mangel).
//   Grenzwert Standard 2 Ohm, je Station aenderbar (Anna-Lena 18.09.2026,
//   z. B. Hamburg 1 Ohm; Spalte station_konfiguration.erder_grenzwert_ohm).

import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import { supabase, withTimeout } from '../lib/supabase';
import { zeileSpeichern, offeneZeilen } from '../lib/warteschlange';
import { useVorWartung } from '../lib/vorWartung';
import { C } from './formui';
import { t } from '../lib/sprache';

const OHM_GRENZE_STANDARD = 2.0;
// Grenzwert der Station, leer/ungueltig = 2 Ohm.
function erderGrenze(wert) {
  const n = Number(String(wert ?? '').replace(',', '.'));
  return wert != null && wert !== '' && isFinite(n) && n > 0 ? n : OHM_GRENZE_STANDARD;
}
const GrenzeContext = createContext(OHM_GRENZE_STANDARD);

// Ohm-Eingabe: Monteure tippen auf dem Tablet mit Komma ("0,12"). Ein
// <input type="number"> nimmt bei deutscher Tastatur kein Komma an — der Wert
// ging dabei still verloren. Deshalb Textfeld mit numerischer Tastatur; hier
// wird zwischen Anzeige (Komma) und Datenbank (Punkt, NUMERIC) umgerechnet.
const ohmToNumber = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return isFinite(n) ? n : null;
};
const ohmToText = (v) =>
  (v === null || v === undefined || v === '') ? '' : String(v).replace('.', ',');

// Schienen und Anschluesse einer Station laden und im Geraet merken. Ohne Netz
// (Keller, Trafostation) arbeitet der Monteur mit dem zuletzt geladenen Stand -
// vorher stand dort "Noch keine Schiene angelegt", Messwerte liessen sich nicht
// eintragen, und ein Tipp auf "+ Neue Schiene" legte eine doppelte an.
const ERDER_CACHE = 'etb-erder-cache-';
export function erderAusCache(stationId) {
  try { return JSON.parse(localStorage.getItem(ERDER_CACHE + stationId) || 'null'); } catch { return null; }
}
export async function erderDatenLaden(stationId) {
  const { data: sData, error: e1 } = await withTimeout(
    supabase.from('station_erderanlage_schienen')
      .select('*').eq('station_id', stationId).eq('active', true).order('reihenfolge'),
    20000, 'Erderanlage laden'
  );
  if (e1) throw e1;
  const ids = (sData || []).map(s => s.id);
  let aData = [];
  if (ids.length > 0) {
    const { data, error: e2 } = await withTimeout(
      supabase.from('station_erderanlage_anschluesse')
        .select('*').in('schiene_id', ids).eq('active', true).order('reihenfolge'),
      20000, 'Anschlüsse laden'
    );
    if (e2) throw e2;
    aData = data || [];
  }
  const schienen = (sData || []).map(s => ({ ...s, anschluesse: aData.filter(a => a.schiene_id === s.id) }));
  try { localStorage.setItem(ERDER_CACHE + stationId, JSON.stringify(schienen)); } catch { /* nur Komfort */ }
  return schienen;
}

// === Hook: Schienen + Anschluesse fuer eine Station laden + CRUD ============
function useSchienen(stationId) {
  const [schienen, setSchienen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  // 'ok' = vom Server, 'cache' = letzter Stand im Geraet, 'fehler' = nichts da
  const [ladeStand, setLadeStand] = useState('ok');
  // Laeuft gerade ein Schreibvorgang? Dann sind die Knoepfe gesperrt - sonst
  // entstehen bei einem Doppeltipp zwei Schienen (so sind schon einmal
  // Stationen achtfach in der Datenbank gelandet).
  const [speichert, setSpeichert] = useState(false);
  const ersteLadung = useRef(true);

  // Nachladen der Schienen.
  //
  // Frueher OHNE Zeitgrenze und OHNE Fehlerfang, und bei JEDEM Nachladen wurde
  // der ganze Block durch "Lade Erderanlage ..." ersetzt. Hing eine Anfrage
  // oder schlug sie fehl, wurde loading nie wieder false - der Block blieb fuer
  // immer auf "Lade Erderanlage ...". Genau das war das Aufhaengen beim
  // Anlegen einer Schiene, in den Stammdaten wie in der Wartung.
  // Jetzt: nur beim ersten Mal "laedt", danach bleibt der Inhalt stehen;
  // Zeitgrenze; und loading wird in jedem Fall wieder zurueckgesetzt.
  const reload = useCallback(async () => {
    if (!stationId) { setLoading(false); return; }
    if (ersteLadung.current) setLoading(true);
    try {
      setSchienen(await erderDatenLaden(stationId));
      setLadeStand('ok');
      setFehler(null);
    } catch (e) {
      const gemerkt = erderAusCache(stationId);
      if (gemerkt) {
        setSchienen(gemerkt);
        setLadeStand('cache');
      } else {
        setLadeStand('fehler');
        setFehler(t('Die Erderanlage konnte nicht geladen werden: {fehler}. Bitte Verbindung prüfen.',
          { fehler: e.message || t('unbekannter Fehler') }));
      }
    } finally {
      setLoading(false);
      ersteLadung.current = false;
    }
  }, [stationId]);

  useEffect(() => { ersteLadung.current = true; }, [stationId]);
  useEffect(() => { reload(); }, [reload]);

  // Jeder Schreibvorgang: mit Zeitgrenze, mit Fehlerpruefung, und waehrend er
  // laeuft sind die Knoepfe gesperrt. Ging etwas schief, steht es rot da.
  // Nacheinander statt verwerfen (v63): Lief schon ein Schreibvorgang, wurde
  // der naechste still ignoriert - ein Namen, der beim Verlassen des Feldes
  // gespeichert werden sollte, war dann weg.
  const kette = useRef(Promise.resolve());
  const schreiben = (bauen, was) => {
    kette.current = kette.current.then(async () => {
      setFehler(null);
      setSpeichert(true);
      try {
        const { error } = await withTimeout(bauen(), 20000, was);
        if (error) throw error;
        await reload();
      } catch (e) {
        setFehler(t('{was} hat nicht geklappt: {fehler}. Bitte prüfen, ob die Verbindung steht, und noch einmal versuchen.',
          { was: t(was), fehler: e.message || t('unbekannter Fehler') }));
      } finally {
        setSpeichert(false);
      }
    });
    return kette.current;
  };

  const addSchiene = (bezeichnung) => {
    const max = schienen.reduce((m, s) => Math.max(m, s.reihenfolge), -1);
    return schreiben(() => supabase.from('station_erderanlage_schienen').insert({
      station_id: stationId,
      bezeichnung: bezeichnung || 'Neue Schiene',
      reihenfolge: max + 1,
    }), 'Schiene anlegen');
  };
  const updateSchiene = (id, patch) =>
    schreiben(() => supabase.from('station_erderanlage_schienen').update(patch).eq('id', id),
      'Schiene aendern');
  const deleteSchiene = (id) =>
    schreiben(() => supabase.from('station_erderanlage_schienen').update({ active: false }).eq('id', id),
      'Schiene loeschen');
  const addAnschluss = (schieneId, bezeichnung) => {
    const schiene = schienen.find(s => s.id === schieneId);
    const max = (schiene?.anschluesse || []).reduce((m, a) => Math.max(m, a.reihenfolge), -1);
    return schreiben(() => supabase.from('station_erderanlage_anschluesse').insert({
      schiene_id: schieneId,
      bezeichnung: bezeichnung || 'Anschluss',
      reihenfolge: max + 1,
    }), 'Anschluss anlegen');
  };
  const updateAnschluss = (id, patch) =>
    schreiben(() => supabase.from('station_erderanlage_anschluesse').update(patch).eq('id', id),
      'Anschluss aendern');
  const deleteAnschluss = (id) =>
    schreiben(() => supabase.from('station_erderanlage_anschluesse').update({ active: false }).eq('id', id),
      'Anschluss loeschen');

  return { schienen, loading, fehler, ladeStand, speichert, reload, addSchiene, updateSchiene, deleteSchiene, addAnschluss, updateAnschluss, deleteAnschluss };
}

// === Hook: Messungen fuer ein Protokoll laden + speichern ===================
function useMessungen(protokollId) {
  const [messungen, setMessungen] = useState({});
  const [fehler, setFehler] = useState(null);
  const [wartet, setWartet] = useState(false);

  // Was noch auf dem Geraet wartet, ist neuer als der Server-Stand.
  const mitOffenen = useCallback((map) => {
    const neu = { ...map };
    for (const p of offeneZeilen('messung')) {
      if (p.protokoll_id === protokollId) neu[p.anschluss_id] = { ...(neu[p.anschluss_id] || {}), ...p };
    }
    return neu;
  }, [protokollId]);

  // Messwerte laden - mit Zeitgrenze und Fehlerfang, wie ueberall sonst auch.
  const reload = useCallback(async () => {
    if (!protokollId) return;
    try {
      const { data, error } = await withTimeout(
        supabase.from('protokoll_erderanlage_messungen').select('*').eq('protokoll_id', protokollId),
        20000, 'Messwerte laden'
      );
      if (error) throw error;
      const map = {};
      (data || []).forEach(m => { map[m.anschluss_id] = m; });
      setMessungen(mitOffenen(map));
    } catch (e) {
      setMessungen(m => mitOffenen(m));
      setFehler(t('Die Messwerte konnten nicht geladen werden: {fehler}.', { fehler: e.message || t('unbekannter Fehler') }));
    }
  }, [protokollId, mitOffenen]);

  useEffect(() => { reload(); }, [reload]);

  // extra: z. B. { ueber_messbereich: true } fuer "OL".
  //
  // Seit v53 ueber die Warteschlange: Ohne Netz steht der Wert sofort im
  // Geraet und geht automatisch raus. Vorher: "Bitte erneut eintragen" - und
  // beim Verlassen der Seite war er weg.
  const saveMessung = async (anschlussId, ohmWert, extra = {}) => {
    if (!protokollId) return;
    const alt = messungen[anschlussId];
    const payload = { protokoll_id: protokollId, anschluss_id: anschlussId, ohm_wert: ohmWert, ...extra };
    // Wer von "OL" auf eine Zahl wechselt, muss OL auch zuruecknehmen.
    if (payload.ueber_messbereich === undefined && alt?.ueber_messbereich) payload.ueber_messbereich = false;
    setMessungen(m => ({ ...m, [anschlussId]: { ...(m[anschlussId] || {}), ...payload } }));
    setFehler(null);
    try {
      const r = await zeileSpeichern('messung', protokollId + ':' + anschlussId, payload);
      setWartet(r.status === 'wartet');
      if (r.status === 'gesendet' && r.data) setMessungen(m => ({ ...m, [anschlussId]: r.data }));
    } catch (e) {
      // Anzeige zuruecknehmen: Sonst zaehlte z. B. ein abgelehntes "OL" weiter
      // als Mangel und setzte 06.3 auf n.i.O. - fuer einen Wert, der nirgends steht.
      setMessungen(m => ({ ...m, [anschlussId]: alt }));
      setFehler(t('Der Messwert wurde von der Datenbank abgelehnt: {fehler}. Bitte im Büro Bescheid geben.',
        { fehler: e.message || t('unbekannter Fehler') }));
    }
  };

  return { messungen, saveMessung, reload, fehler, wartet };
}

// === Anschluss-Pin (SVG Element + Edit-Felder) ==============================
function AnschlussPin({ anschluss, messung, isProtokoll, onUpdateAnschluss, onDeleteAnschluss, onSaveMessung, xCenter, schieneY }) {
  const [localOhm, setLocalOhm] = useState(ohmToText(messung?.ohm_wert));
  // Wert der letzten Wartung (nur Anzeige, Vorschlag 7)
  const vorher = useVorWartung()?.erder?.[anschluss.id] || null;
  const [localBezeichnung, setLocalBezeichnung] = useState(anschluss.bezeichnung);
  const [editingName, setEditingName] = useState(false);

  useEffect(() => { setLocalOhm(ohmToText(messung?.ohm_wert)); }, [messung?.ohm_wert]);
  useEffect(() => { setLocalBezeichnung(anschluss.bezeichnung); }, [anschluss.bezeichnung]);

  // "OL" (Overload / Over Limit): Das Messgeraet zeigt OL, wenn der Widerstand
  // ueber seinem Messbereich liegt - praktisch KEINE leitende Verbindung
  // (Leiter unterbrochen, Klemme lose/korrodiert). Das ist weit ueber 2 Ohm,
  // also ein Mangel. Vorher liessen sich nur Zahlen eintragen.
  const istOl = !!messung?.ueber_messbereich;
  const ohmValue = istOl ? null : ohmToNumber(localOhm);
  const OHM_GRENZE = useContext(GrenzeContext);
  const isMangel = istOl || (ohmValue !== null && ohmValue > OHM_GRENZE);
  const isOk = !istOl && ohmValue !== null && ohmValue <= OHM_GRENZE;

  const pinColor = isMangel ? C.red : isOk ? C.green : C.gray500;
  const pinBg = isMangel ? '#FDECEA' : isOk ? '#E5F4ED' : C.white;

  const commitOhm = () => {
    if (istOl) return;
    const neu = ohmToNumber(localOhm);
    const bisher = messung ? ohmToNumber(messung.ohm_wert) : null;
    if (neu === bisher) return;   // nichts geaendert
    onSaveMessung(anschluss.id, neu);
  };
  const olUmschalten = () => {
    if (istOl) {
      onSaveMessung(anschluss.id, null, { ueber_messbereich: false });
    } else {
      setLocalOhm('');
      onSaveMessung(anschluss.id, null, { ueber_messbereich: true });
    }
  };
  const commitName = () => {
    if (localBezeichnung !== anschluss.bezeichnung) {
      onUpdateAnschluss(anschluss.id, { bezeichnung: localBezeichnung });
    }
    setEditingName(false);
  };

  return (
    <g>
      {/* Vertikale Linie vom Anschluss zur Schiene */}
      <line x1={xCenter} y1={schieneY} x2={xCenter} y2={schieneY + 30} stroke={pinColor} strokeWidth="2"/>
      {/* Erder-Symbol (drei waagrechte Striche) unter der Linie */}
      <line x1={xCenter - 14} y1={schieneY + 30} x2={xCenter + 14} y2={schieneY + 30} stroke={pinColor} strokeWidth="2.5"/>
      <line x1={xCenter - 10} y1={schieneY + 34} x2={xCenter + 10} y2={schieneY + 34} stroke={pinColor} strokeWidth="2"/>
      <line x1={xCenter - 6}  y1={schieneY + 38} x2={xCenter + 6}  y2={schieneY + 38} stroke={pinColor} strokeWidth="2"/>
      {/* Punkt am Anschluss-Punkt */}
      <circle cx={xCenter} cy={schieneY} r={5} fill={pinColor}/>
      <foreignObject x={xCenter - 84} y={schieneY + 48} width={168} height={250}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{ background: pinBg, border: `1.5px solid ${pinColor}`, borderRadius: 8, padding: 6, fontSize: 13 }}>
          {editingName ? (
            <input
              type="text"
              value={localBezeichnung}
              onChange={e => setLocalBezeichnung(e.target.value)}
              onBlur={commitName}
              onKeyDown={e => { if (e.key === 'Enter') commitName(); }}
              autoFocus
              style={{ width: '100%', fontSize: 14, fontWeight: 700, color: C.navy, border: `1px solid ${C.gray200}`, padding: '2px 4px', borderRadius: 4, boxSizing: 'border-box' }}
            />
          ) : (
            <div onClick={() => setEditingName(true)} style={{ fontWeight: 700, color: C.navy, fontSize: 14, marginBottom: 4, cursor: 'pointer', textAlign: 'center' }}>
              {anschluss.bezeichnung || '–'}
            </div>
          )}
          {isProtokoll && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4 }}>
              <input
                type="text"
                inputMode="decimal"
                value={istOl ? 'OL' : localOhm}
                disabled={istOl}
                onChange={e => setLocalOhm(e.target.value.replace(/[^0-9.,]/g, ''))}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                onBlur={commitOhm}
                placeholder="Ohm"
                style={{ width: '52%', minHeight: 36, fontSize: 16, padding: '4px 6px', border: `1.5px solid ${pinColor}`, borderRadius: 6, color: pinColor, fontWeight: 700, boxSizing: 'border-box', background: istOl ? C.redBg : C.white }}
              />
              <span style={{ fontSize: 13, color: pinColor, fontWeight: 700 }}>Ω</span>
              <button type="button" onClick={olUmschalten}
                title={t('OL = über Messbereich (keine Verbindung)')}
                style={{ marginLeft: 'auto', minHeight: 36, minWidth: 40, padding: '2px 8px', borderRadius: 6, fontSize: 14, fontWeight: 800,
                         border: '1.5px solid ' + (istOl ? C.red : C.gray300), background: istOl ? C.red : C.white,
                         color: istOl ? C.white : C.gray700, cursor: 'pointer' }}>
                OL
              </button>
            </div>
          )}
          {isProtokoll && vorher && (
            <div style={{ marginTop: 3, fontSize: 12, color: C.gray500, textAlign: 'center' }}>
              {t('zuletzt')}: {vorher.ol ? 'OL' : String(vorher.ohm).replace('.', ',') + ' Ω'}
            </div>
          )}
          {istOl && (
            <div style={{ marginTop: 4, fontSize: 12, color: C.red, fontWeight: 700, lineHeight: 1.3 }}>
              {t('OL = über Messbereich, keine Verbindung → Mangel')}
            </div>
          )}
          {isMangel && !istOl && (
            <div style={{ marginTop: 4, fontSize: 12, color: C.red, fontWeight: 700 }}>{t('! Mangel (über {n} Ω)', { n: String(OHM_GRENZE).replace('.', ',') })}</div>
          )}
          {/* Nachfragen statt verbieten: ein Komma an der falschen Stelle macht
              aus 0,82 schnell 82 - das faellt sonst erst im Buero auf. */}
          {ohmValue !== null && ohmValue > 100 && (
            <div style={{ marginTop: 4, fontSize: 12, color: C.orangeText, fontWeight: 700, lineHeight: 1.3 }}>
              ⚠ {t('Über 100 Ω — Komma prüfen?')}
            </div>
          )}
          {/* Im Protokoll NICHT loeschbar (Pruefung 20.09.2026): Der Knopf klebte
              unter dem Ohm-Feld, legt den Anschluss aber dauerhaft in den
              Stammdaten still - fuer alle kuenftigen Wartungen. Das macht das Buero. */}
          {!isProtokoll && (
            <button
              onClick={() => { if (window.confirm(t('Anschluss "{name}" wirklich löschen?', { name: anschluss.bezeichnung }))) onDeleteAnschluss(anschluss.id); }}
              style={{ marginTop: 4, minHeight: 32, fontSize: 13, background: 'none', border: 'none', color: C.gray700, cursor: 'pointer', padding: '4px 6px', textDecoration: 'underline' }}
            >
              🗑️ {t('löschen')}
            </button>
          )}
        </div>
      </foreignObject>
    </g>
  );
}

// === Eine einzelne Schiene mit Anschluessen (Grafik + Edit) =================
function SchienenCard({ schiene, messungen, isProtokoll, onUpdateSchiene, onDeleteSchiene, onAddAnschluss, onUpdateAnschluss, onDeleteAnschluss, onSaveMessung, speichert }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(schiene.bezeichnung);
  const [newAnschlussName, setNewAnschlussName] = useState('');

  useEffect(() => { setName(schiene.bezeichnung); }, [schiene.bezeichnung]);

  const anschluesse = schiene.anschluesse || [];
  const n = anschluesse.length;
  // SVG-Layout
  // Abstaende an die groesseren Schriften angepasst (03.09.2026): Der Kasten
  // am Anschluss ist jetzt 168 statt 150 breit, die Pins muessen weiter
  // auseinander stehen, sonst ueberlappen die Beschriftungen.
  const padding = 66;
  const minWidth = 400;
  const stepX = 150;
  const totalWidth = Math.max(minWidth, padding * 2 + Math.max(0, n - 1) * stepX);
  const svgHeight = 330;   // mehr Hoehe: groessere Schriften und der OL-Knopf brauchen Platz
  const schieneY = 60;

  const commitName = () => {
    if (name !== schiene.bezeichnung) onUpdateSchiene(schiene.id, { bezeichnung: name });
    setEditingName(false);
  };
  const handleAdd = () => {
    if (speichert) return;
    const v = newAnschlussName.trim() || 'Anschluss ' + (n + 1);
    onAddAnschluss(schiene.id, v);
    setNewAnschlussName('');
  };

  return (
    <div style={{ background: C.white, border: `2px solid ${C.gray200}`, borderRadius: 12, padding: 16, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid ' + C.gray100 }}>
        {editingName ? (
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={e => { if (e.key === 'Enter') commitName(); }}
            autoFocus
            style={{ fontSize: 16, fontWeight: 800, color: C.navy, padding: '4px 8px', border: `1.5px solid ${C.gray200}`, borderRadius: 6 }}
          />
        ) : (
          <div onClick={() => setEditingName(true)} style={{ fontWeight: 800, color: C.navy, fontSize: 16, cursor: 'pointer' }}>
            ⚡ {schiene.bezeichnung || t('Schiene')}
          </div>
        )}
        <button
          onClick={() => { if (window.confirm(t('Schiene "{name}" mit allen Anschlüssen wirklich löschen?', { name: schiene.bezeichnung }))) onDeleteSchiene(schiene.id); }}
          style={{ minHeight: 44, fontSize: 14, background: C.white, border: `1.5px solid ${C.gray200}`, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', color: C.red, fontWeight: 700 }}
        >
          🗑️ {t('Schiene löschen')}
        </button>
      </div>

      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <svg width={totalWidth} height={svgHeight} style={{ display: 'block' }}>
          {/* Hauptschiene (waagrechter Balken) */}
          <rect x={20} y={schieneY - 8} width={totalWidth - 40} height={16} fill={C.gray700} rx={3}/>
          {/* Verbindungs-Marker an den Enden */}
          <circle cx={20} cy={schieneY} r={6} fill={C.gray700}/>
          <circle cx={totalWidth - 20} cy={schieneY} r={6} fill={C.gray700}/>
          {n === 0 && (
            <text x={totalWidth / 2} y={schieneY + 100} textAnchor="middle" fill={C.gray500} fontSize="13" fontStyle="italic">
              {t('Noch keine Anschlüsse — unten hinzufügen')}
            </text>
          )}
          {anschluesse.map((a, i) => {
            const xCenter = padding + i * stepX;
            return (
              <AnschlussPin
                key={a.id}
                anschluss={a}
                messung={messungen[a.id]}
                isProtokoll={isProtokoll}
                onUpdateAnschluss={onUpdateAnschluss}
                onDeleteAnschluss={onDeleteAnschluss}
                onSaveMessung={onSaveMessung}
                xCenter={xCenter}
                schieneY={schieneY}
              />
            );
          })}
        </svg>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 10, background: C.gray100, borderRadius: 8 }}>
        <input
          type="text"
          value={newAnschlussName}
          onChange={e => setNewAnschlussName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          placeholder={t('z.B. HSE, Trafo, Mast …')}
          style={{ flex: 1, minWidth: 0, padding: '10px 10px', border: `1.5px solid ${C.gray200}`, borderRadius: 6, fontSize: 16 }}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={speichert}
          style={{ minHeight: 44, padding: '9px 16px', background: speichert ? C.gray300 : C.yellow, color: C.navy, border: 'none', borderRadius: 6, fontWeight: 800, cursor: speichert ? 'wait' : 'pointer', fontSize: 15, whiteSpace: 'nowrap' }}
        >
          {speichert ? t('speichert …') : t('+ Anschluss')}
        </button>
      </div>
    </div>
  );
}

// === Hauptkomponente ========================================================
// onBefund (optional): meldet dem Aufrufer laufend, wie die Messung ausfaellt.
// Damit kann EditableStammdaten den Pruefpunkt 06.3 automatisch bewerten.
// grenzeOhm: Wert aus der Stationskonfiguration (leer = 2 Ohm).
// grenzeAendern: wenn gesetzt, ist der Grenzwert hier einstellbar.
// onMessungGeaendert: jemand hat in DIESER Sitzung einen Messwert geaendert - dann
// darf die Auto-Bewertung 06.3 auch in einem abgeschlossenen Protokoll nachziehen.
export default function ErderanlageBlock({ stationId, protokollId, userId, onBefund, grenzeOhm, grenzeAendern, onMessungGeaendert }) {
  // Ohne Station gibt es nichts anzulegen: Eine Schiene haengt an einer
  // Station. Bei einer neuen, noch nicht gespeicherten Station war das Feld
  // trotzdem da - der Klick lief dann in "null value in column station_id".
  if (!stationId) {
    return (
      <div style={{ padding: '12px 14px', background: C.yellowBg,
                    borderLeft: '4px solid ' + C.yellow, borderRadius: 6,
                    fontSize: 15, color: C.navy, lineHeight: 1.45 }}>
        {t('Bitte die Station zuerst')} <strong>{t('speichern')}</strong>{t('. Danach lassen sich hier die Schienen und Anschlüsse der Erderanlage anlegen.')}
      </div>
    );
  }
  return <ErderanlageInhalt stationId={stationId} protokollId={protokollId}
                            userId={userId} onBefund={onBefund}
                            grenzeOhm={grenzeOhm} grenzeAendern={grenzeAendern}
                            onMessungGeaendert={onMessungGeaendert} />;
}

// Der eigentliche Inhalt. Bewusst als eigene Komponente: Die Hooks duerfen
// erst laufen, wenn eine Station feststeht - React erlaubt kein bedingtes
// Aufrufen von Hooks in derselben Komponente.
function ErderanlageInhalt({ stationId, protokollId, userId, onBefund, grenzeOhm, grenzeAendern, onMessungGeaendert }) {
  const OHM_GRENZE = erderGrenze(grenzeOhm);
  const { schienen, loading, fehler: schienenFehler, ladeStand, reload: schienenNeuLaden, speichert, addSchiene, updateSchiene, deleteSchiene, addAnschluss, updateAnschluss, deleteAnschluss } = useSchienen(stationId);
  const { messungen, saveMessung, fehler: messFehler, wartet: messWartet } = useMessungen(protokollId);
  const [newSchieneName, setNewSchieneName] = useState('');

  const isProtokoll = !!protokollId;

  // Messbefund: wie viele Anschluesse sind gemessen, wie viele ueber 2 Ohm,
  // und welcher ist der schlechteste. Grundlage fuer Anzeige UND fuer die
  // automatische Bewertung von Pruefpunkt 06.3 (Erdungswiderstandsmessung).
  const befund = useMemo(() => {
    let anzahlGemessen = 0;
    let anzahlUeberGrenze = 0;
    let maxOhm = null;
    let maxOl = false;           // mindestens ein Anschluss mit "OL"
    let schlechtesterAnschluss = null;
    for (const s of schienen) {
      for (const a of (s.anschluesse || [])) {
        if (messungen[a.id]?.ueber_messbereich) {
          // OL ist schlechter als jeder Zahlenwert.
          anzahlGemessen++;
          anzahlUeberGrenze++;
          if (!maxOl) { maxOl = true; schlechtesterAnschluss = a.bezeichnung || 'Anschluss'; }
          continue;
        }
        const wert = ohmToNumber(messungen[a.id]?.ohm_wert);
        if (wert === null) continue;
        anzahlGemessen++;
        if (!maxOl && (maxOhm === null || wert > maxOhm)) {
          maxOhm = wert;
          schlechtesterAnschluss = a.bezeichnung || 'Anschluss';
        }
        if (wert > OHM_GRENZE) anzahlUeberGrenze++;
      }
    }
    return { anzahlGemessen, anzahlUeberGrenze, maxOhm, maxOl, schlechtesterAnschluss, grenze: OHM_GRENZE };
  }, [schienen, messungen, OHM_GRENZE]);

  const mangelAnzahl = befund.anzahlUeberGrenze;

  useEffect(() => { if (onBefund) onBefund(befund); }, [befund, onBefund]);

  if (loading) return <div style={{ padding: 16, color: C.gray500 }}>{t('Lade Erderanlage …')}</div>;

  const stoerung = messFehler || schienenFehler;

  return (
    <GrenzeContext.Provider value={OHM_GRENZE}>
    <div style={{ marginBottom: 16 }}>
      {/* Rot und deutlich: Ein nicht gespeicherter Ohm-Wert darf nicht untergehen. */}
      {stoerung && (
        <div style={{ padding: '12px 14px', background: C.redBg, border: '2px solid ' + C.red,
                      borderRadius: 8, marginBottom: 12, fontSize: 15, color: C.red,
                      fontWeight: 700, lineHeight: 1.45 }}>
          {stoerung}
        </div>
      )}
      {messWartet && !messFehler && (
        <div style={{ padding: '10px 14px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow,
                      borderRadius: 6, marginBottom: 12, fontSize: 15, color: C.navy }}>
          {t('Kein Netz: Die Messwerte sind auf dem Gerät gespeichert und werden automatisch gesendet.')}
        </div>
      )}
      <div style={{ padding: '10px 14px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6, marginBottom: 14, fontSize: 14, color: C.gray700 }}>
        ⚡ <strong>{t('Erderanlage / Hauptpotentialausgleich.')}</strong> {t('Schienen und Anschlüsse werden in den Stammdaten der Station gespeichert. Der Ohm-Wert wird pro Wartung neu gemessen.')} <strong>{t('Bei > {grenze} Ω oder „OL“ wird automatisch ein Mangel angelegt.', { grenze: String(OHM_GRENZE).replace('.', ',') })}</strong> {t('„OL“ zeigt das Messgerät, wenn der Wert über dem Messbereich liegt – dann besteht keine leitende Verbindung.')}
        {mangelAnzahl > 0 && <span style={{ marginLeft: 6, color: C.red, fontWeight: 700 }}>{t('· Aktuell {n} Mangel/Mängel über Grenzwert.', { n: mangelAnzahl })}</span>}
      </div>

      <GrenzwertFeld wert={grenzeOhm} grenze={OHM_GRENZE} onAendern={grenzeAendern}/>

      {ladeStand !== 'ok' && (
        <div style={{ padding: '10px 14px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6,
                      marginBottom: 12, fontSize: 15, color: C.navy, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            {ladeStand === 'cache'
              ? t('Kein Netz: Die Schienen werden aus dem Gerät angezeigt. Messwerte lassen sich eintragen, neue Schienen erst mit Netz anlegen.')
              : t('Die Schienen konnten nicht geladen werden (kein Netz?).')}
          </span>
          <button type="button" onClick={() => schienenNeuLaden()}
            style={{ minHeight: 44, padding: '8px 14px', borderRadius: 7, border: '1.5px solid ' + C.navy, background: C.white, color: C.navy, fontWeight: 700, cursor: 'pointer' }}>
            {t('Nochmal laden')}
          </button>
        </div>
      )}

      {schienen.length === 0 && ladeStand === 'ok' && (
        <div style={{ padding: 16, background: C.gray100, borderRadius: 8, color: C.gray500, marginBottom: 12, fontStyle: 'italic', textAlign: 'center' }}>
          {t('Noch keine Schiene angelegt. Unten neue Schiene hinzufügen.')}
        </div>
      )}

      {schienen.map(s => (
        <SchienenCard
          key={s.id}
          schiene={s}
          messungen={messungen}
          isProtokoll={isProtokoll}
          onUpdateSchiene={updateSchiene}
          onDeleteSchiene={deleteSchiene}
          onAddAnschluss={addAnschluss}
          onUpdateAnschluss={updateAnschluss}
          onDeleteAnschluss={deleteAnschluss}
          onSaveMessung={(...args) => { if (onMessungGeaendert) onMessungGeaendert(); return saveMessung(...args); }}
          speichert={speichert}
        />
      ))}

      {ladeStand === 'ok' && (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 12, background: C.navy, borderRadius: 8 }}>
        <input
          type="text"
          value={newSchieneName}
          onChange={e => setNewSchieneName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !speichert) { addSchiene(newSchieneName.trim() || ('Schiene ' + (schienen.length + 1))); setNewSchieneName(''); } }}
          placeholder={t('Neue Schiene (z.B. Hauptpotentialausgleich)')}
          style={{ flex: 1, minWidth: 0, padding: '11px 12px', border: 'none', borderRadius: 6, fontSize: 16 }}
        />
        <button
          type="button"
          disabled={speichert}
          onClick={() => { addSchiene(newSchieneName.trim() || ('Schiene ' + (schienen.length + 1))); setNewSchieneName(''); }}
          style={{ minHeight: 46, padding: '10px 18px', background: speichert ? C.gray300 : C.yellow, color: C.navy, border: 'none', borderRadius: 6, fontWeight: 800, cursor: speichert ? 'wait' : 'pointer', fontSize: 15, whiteSpace: 'nowrap' }}
        >
          {speichert ? t('wird angelegt …') : t('+ Neue Schiene')}
        </button>
      </div>
      )}
    </div>
    </GrenzeContext.Provider>
  );
}

// Grenzwert der Erdungswiderstandsmessung. Standard 2 Ohm; nur aendern, wenn der
// Netzbetreiber etwas anderes verlangt. Gespeichert wird beim Verlassen des Felds.
function GrenzwertFeld({ wert, grenze, onAendern }) {
  const alsText = (w) => (w == null || w === '' ? '' : String(w).replace('.', ','));
  const [text, setText] = useState(alsText(wert));
  const [fehler, setFehler] = useState(null);
  useEffect(() => { setText(alsText(wert)); }, [wert]);
  const abweichend = grenze !== OHM_GRENZE_STANDARD;
  if (!onAendern) {
    return abweichend ? (
      <div style={{ fontSize: 14, color: C.navy, marginBottom: 12 }}>
        <strong>{t('Grenzwert dieser Station: {grenze} Ω', { grenze: String(grenze).replace('.', ',') })}</strong>
      </div>
    ) : null;
  }
  const uebernehmen = () => {
    const roh = text.trim();
    if (roh === '') { if (wert != null && wert !== '') onAendern(null); return; }
    const n = Number(roh.replace(',', '.'));
    if (!isFinite(n) || n < 0.01 || n > 100) { setFehler(t('Bitte einen Wert zwischen 0,01 und 100 Ω eintragen.')); return; }
    setFehler(null);
    if (Number(wert) !== n) onAendern(n);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14, fontSize: 14, color: C.gray700 }}>
      <label htmlFor="erder-grenzwert" style={{ fontWeight: 700, color: C.navy }}>{t('Grenzwert Erdungswiderstand')}</label>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span>≤</span>
        <input id="erder-grenzwert" type="text" inputMode="decimal" value={text} placeholder="2,0"
          onChange={e => setText(e.target.value)} onBlur={uebernehmen}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          style={{ width: 70, padding: '8px 10px', fontSize: 16, border: '1.5px solid ' + (abweichend ? C.orange : C.gray200), borderRadius: 6 }}/>
        <span>Ω</span>
      </span>
      <span style={{ color: fehler ? C.red : C.gray500, fontWeight: fehler ? 700 : 400 }}>
        {fehler || (abweichend ? t('abweichend vom Standard 2 Ω') : t('Standard 2 Ω – nur ändern, wenn der Netzbetreiber etwas anderes fordert (z. B. Hamburg 1 Ω)'))}
      </span>
    </div>
  );
}
